import { registerSW } from 'virtual:pwa-register';
import { readAppVersion } from '@/lib/appVersion';
import { api } from '@/lib/api/client';
import { currentSubscription, ensurePushSubscribed, pushSupported } from '@/lib/push';

/**
 * PWA 업데이트 감지와 적용. (2026-08-30, docs/impl/89)
 *
 * 🔴 왜 필요했나 - 폰에서 배포가 반영되지 않았다
 *   홈 화면에 추가한 앱은 껐다 켜도 OS 가 잠든 앱을 깨울 뿐 페이지 이동이 없다.
 *   서비스 워커는 페이지 이동 때 새 버전을 확인하므로, 확인 요청 자체가
 *   며칠씩 안 나갔다. 확인이 되더라도 이미 실행 중인 자바스크립트는 옛것이라
 *   앱을 완전히 종료해야만 화면이 바뀌었다.
 *
 *   그래서 사용자는 홈 화면에서 제거 → 설정에서 사이트 데이터 삭제 →
 *   사파리로 다시 추가하는 절차를 매번 밟고 있었다.
 *
 * 🔴 감지를 두 겹으로 둔다 (2026-08-30 보강)
 *   ① 서비스 워커  - registration.update() 를 직접 걸고 onNeedRefresh 를 받는다
 *   ② 버전 파일    - /version.json 을 받아 이 번들의 버전과 견준다
 *
 *   ①만 두면 iOS 가 조용할 때 앱이 새 배포의 존재 자체를 모른다.
 *   ②는 서비스 워커를 거치지 않는 평범한 fetch 라, 워커가 침묵해도 잡아낸다.
 *   대신 ②로 잡은 것은 워커가 놓친 것이므로 평범한 적용으로는 안 바뀐다 -
 *   그때는 캐시를 비우는 강제 갱신으로 간다. (applyNow 참조)
 *
 * 🔴 새로고침이 로그아웃은 아니다
 *   세션은 서버(Redis)에 있고 쿠키는 그대로다. 다시 로그인할 필요가 없다.
 *   업데이트마다 로그인을 요구하면 폰에서 금방 피로해진다.
 */

/** 업데이트 확인 주기. 앱을 오래 열어 둔 채로도 새 버전을 잡는다. */
const CHECK_INTERVAL_MS = 30 * 60 * 1000;

/** 배너를 띄우고 새로고침하기까지의 여유. 사용자가 무슨 일인지 읽을 시간이다. */
export const AUTO_RELOAD_DELAY_MS = 4000;

/**
 * 서비스 워커 경로로 새로고침한 뒤, 이 시간 안에 또 잡히면 자동 적용하지 않는다.
 * 2분은 "정상이라면 갱신이 끝났어야 할 시간"이다. 그 안에 또 뜬다는 것은
 * 적용이 실패하고 있다는 뜻이다.
 */
const SW_RELOAD_COOLDOWN_MS = 2 * 60 * 1000;

/**
 * 서비스 워커에 적용을 건 뒤 이 시간 안에 새로고침이 일어나지 않으면
 * 강제 갱신으로 넘어간다. (2026-09-02 사용자 신고)
 *
 * 🔴 왜 필요한가 - [지금 새로고침] 이 아무 반응이 없었다
 *   워커 경로는 SKIP_WAITING 을 보내고 제어권이 넘어오기를 기다린다.
 *   그 controllerchange 가 끝내 오지 않는 경우가 있다(대기 워커가 활성화되지
 *   못하거나, 페이지가 애초에 워커의 제어를 받고 있지 않은 경우다).
 *   그러면 applying 만 true 인 채로 영영 멈춘다 - 화면에는
 *   "새로고침하는 중입니다…" 가 떠 있고 버튼은 잠겨 아무것도 할 수 없다.
 *   사용자는 홈으로 가서 [최신으로 업데이트] 를 눌러야만 빠져나올 수 있었다.
 *
 *   그 탈출을 자동으로 한다. 3.5초는 정상이라면 이미 새로고침이 끝났을 시간이다.
 *   실제로 새로고침되면 페이지와 함께 이 타이머도 사라지므로 헛돌지 않는다.
 */
const SW_APPLY_WATCHDOG_MS = 3500;

/** 이미 강제 갱신을 시도한 목표 버전. 같은 버전으로 두 번 하지 않는다. */
const KEY_FORCED_VERSION = 'bada:update:forced-version';
/** 서비스 워커 경로로 마지막에 새로고침한 시각(ms). */
const KEY_SW_RELOAD_AT = 'bada:update:sw-reload-at';
/** 갱신 직전 버전. 새로고침 뒤 "무엇이 바뀌었나" 를 알리는 데 쓴다. */
const KEY_PREV_VERSION = 'bada:update:prev-version';

type Listener = (state: UpdateState) => void;

/** 새 버전을 무엇이 잡았나. 적용 방법이 갈린다. */
export type UpdateSource = 'sw' | 'version';

export interface UpdateState {
  /** 새 버전이 준비됐다. 배너를 띄운다 */
  available: boolean;
  /** 적용 중(새로고침 직전). 버튼을 잠근다 */
  applying: boolean;
  /**
   * sw      서비스 워커가 새 버전을 대기시켰다 - 평범한 적용으로 끝난다
   * version 버전 파일이 어긋났다 - 워커가 놓친 것이라 강제 갱신이 필요하다
   */
  source: UpdateSource | null;
  /**
   * 자동으로 적용해도 되나. 직전 시도가 먹히지 않은 흔적이 있으면 false 이고,
   * 그때는 카운트다운 없이 사용자가 누르기를 기다린다. (무한 새로고침 방지)
   */
  auto: boolean;
  /**
   * 받게 될 새 버전. 버전 파일이 잡았을 때만 안다.
   *
   * 서비스 워커 경로에서는 null 이다 - 워커는 "새 것이 있다" 만 알려주고
   * 그것이 몇 번인지는 알려주지 않는다. 그때는 번호 없이 안내한다.
   */
  version: string | null;
}

let state: UpdateState = {
  available: false, applying: false, source: null, auto: true, version: null,
};
const listeners = new Set<Listener>();

/** 새 버전을 적용하는 함수. 서비스 워커 등록 후에 채워진다. */
let applyUpdate: ((reload?: boolean) => Promise<void>) | null = null;

/** 버전 파일이 알려준 목표 버전. 강제 갱신 가드에 쓴다. */
let pendingVersion: string | null = null;

function emit(next: Partial<UpdateState>): void {
  state = { ...state, ...next };
  listeners.forEach((listener) => listener(state));
}

export function subscribeUpdate(listener: Listener): () => void {
  listeners.add(listener);
  listener(state);
  return () => listeners.delete(listener);
}

export function getUpdateState(): UpdateState {
  return state;
}

/**
 * 서버가 주는 version.json 과 이 화면의 버전을 견줘, 도달해야 할 버전을 낸다.
 * 갱신이 필요 없으면 null.
 *
 * @param local     이 번들에 박힌 버전. 로컬 개발에서는 null 이라 비교하지 않는다
 * @param served    /version.json 을 파싱한 것. 형식이 어긋나면 무시한다
 * @param forcedFor 이미 이 버전으로 강제 갱신을 한 번 시도했다면 그 버전
 *
 * 🔴 forcedFor 가 무한 새로고침을 막는다
 *   강제 갱신이 어떤 이유로 실패하면 새로고침한 화면의 버전이 그대로다.
 *   가드가 없으면 곧바로 또 갱신을 걸어 앱이 새로고침만 반복한다.
 *   한 번 시도한 버전은 다시 시도하지 않고, 사용자에게 맡긴다.
 */
export function versionUpdateTarget(
  local: string | null,
  served: unknown,
  forcedFor: string | null,
): string | null {
  if (local === null) {
    return null;
  }
  if (typeof served !== 'object' || served === null) {
    return null;
  }
  const version = (served as { version?: unknown }).version;
  if (typeof version !== 'string' || version.trim() === '') {
    return null;
  }
  if (version === local || version === forcedFor) {
    return null;
  }
  return version;
}

/**
 * 서비스 워커 경로로 자동 새로고침해도 되나.
 *
 * 🔴 이것이 없으면 무한 새로고침이 가능하다
 *   skipWaiting 이 실패하면 새로고침한 화면에서도 새 워커가 여전히 대기 중이라
 *   onNeedRefresh 가 또 뜬다. 그대로 두면 몇 초마다 새로고침이 반복돼
 *   앱을 아예 못 쓰게 된다. 폰에서 이 상태가 되면 손쓸 방법이 마땅치 않다.
 *
 * @param lastReloadAt sessionStorage 에 적힌 직전 새로고침 시각. 없으면 null
 */
export function swAutoReloadAllowed(lastReloadAt: string | null, now: number): boolean {
  if (lastReloadAt === null) {
    return true;
  }
  const at = Number(lastReloadAt);
  if (!Number.isFinite(at)) {
    return true;
  }
  return now - at > SW_RELOAD_COOLDOWN_MS;
}

/**
 * 새 버전을 지금 적용한다. 배너의 버튼과 자동 타이머가 부른다.
 *
 * 잡은 경로에 따라 방법이 갈린다
 *   sw      서비스 워커에 SKIP_WAITING 을 보낸다. 제어권이 넘어오면 새로고침된다
 *   version 워커가 놓친 것이라 SKIP_WAITING 을 보낼 대상이 없다.
 *           캐시를 비우는 강제 갱신으로 간다
 *
 * 🔴 두 번째 누름은 곧바로 강제 갱신이다 (2026-09-02)
 *   전에는 applying 중이면 조용히 반환했다. 워커 경로가 멈춰 있을 때
 *   버튼을 다시 눌러도 아무 일이 없었던 이유다. 이미 한 번 시도한 뒤
 *   또 누른다는 것은 "안 되고 있다" 는 사용자의 신호이므로 그대로 받는다.
 */
export async function applyNow(): Promise<void> {
  if (state.applying) {
    // 워커 경로가 멈춰 있다. 기다리지 말고 캐시를 비우는 길로 간다
    await forceRefresh();
    return;
  }
  emit({ applying: true });
  rememberCurrentVersion();

  if (state.source === 'version' || applyUpdate === null) {
    if (pendingVersion !== null) {
      writeSession(KEY_FORCED_VERSION, pendingVersion);
    }
    await forceRefresh();
    return;
  }

  writeSession(KEY_SW_RELOAD_AT, String(Date.now()));

  /*
    🔴 워커가 응답하지 않으면 스스로 빠져나온다 (2026-09-02)

    applyUpdate(true) 는 SKIP_WAITING 을 보내고 controllerchange 를 기다린다.
    그것이 오지 않으면 이 Promise 는 영영 끝나지 않고 화면은 잠긴 채로 남는다.
    새로고침이 실제로 일어나면 페이지와 함께 이 타이머도 사라지므로,
    타이머가 살아서 발화했다는 것 자체가 "안 됐다" 는 증거다.
  */
  window.setTimeout(() => {
    void forceRefresh();
  }, SW_APPLY_WATCHDOG_MS);

  await applyUpdate(true);
}

/**
 * 서비스 워커를 등록하고 업데이트 확인을 건다. main.tsx 가 한 번 부른다.
 *
 * 🔴 확인을 거는 시점이 핵심이다
 *   visibilitychange - 폰에서 앱을 다시 열었을 때. 이것이 없어서 안 걸렸다
 *   focus           - PC 에서 다른 창을 보다 돌아왔을 때
 *   주기            - 앱을 계속 열어 둔 채로 배포가 나간 경우
 *   등록 직후       - 첫 진입에서 이미 어긋나 있는 경우
 *
 * 🔴 리스너를 registerSW 콜백 밖에 둔다
 *   안에 두면 서비스 워커 등록이 실패했을 때 버전 확인까지 함께 죽는다.
 *   워커가 말썽일 때야말로 버전 파일 그물이 필요하므로 서로 독립이어야 한다.
 *
 * 오프라인이면 확인하지 않는다. 실패 로그만 쌓인다.
 */
export function startUpdateWatch(): void {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') {
    return;
  }

  let registration: ServiceWorkerRegistration | undefined;

  const check = () => {
    if (!navigator.onLine) {
      return;
    }
    if (registration) {
      void registration.update();
    }
    void checkVersionFile();
  };

  if ('serviceWorker' in navigator) {
    applyUpdate = registerSW({
      immediate: true,
      onNeedRefresh() {
        emit({
          available: true,
          source: 'sw',
          auto: swAutoReloadAllowed(readSession(KEY_SW_RELOAD_AT), Date.now()),
        });
      },
      onRegisteredSW(_swUrl, reg) {
        registration = reg;
        /*
          🔴 켜뒀던 알림이 끊겼으면 되살린다 (2026-09-04 사용자 신고)

            강제 갱신이 워커를 해제하면 푸시 구독이 함께 죽는다.
            여기가 워커가 다시 등록된 직후라 구독을 만들 수 있는 첫 시점이다.

            아래 startup 호출과 둘 다 두는 것은 일부러다 - 이 콜백이
            어떤 이유로 안 뜨더라도 복구가 한 번은 시도되어야 한다.
            ensurePushSubscribed 는 멱등이라 두 번 불려도 안전하다.
        */
        void ensurePushSubscribed();
      },
    });
  }

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      check();
    }
  });
  window.addEventListener('focus', check);
  window.setInterval(check, CHECK_INTERVAL_MS);
  check();

  // 워커 등록 콜백과 별개로 한 번 더. 위 주석 참조 (멱등)
  void ensurePushSubscribed();
}

/**
 * 서버의 version.json 과 이 번들의 버전을 견준다.
 *
 * 서비스 워커가 이미 새 버전을 잡았으면 아무것도 하지 않는다 -
 * 그쪽이 캐시를 지우지 않는 가벼운 경로라 우선한다.
 */
async function checkVersionFile(): Promise<void> {
  if (state.available) {
    return;
  }

  const local = readAppVersion(import.meta.env)?.version ?? null;
  if (local === null) {
    // 로컬 개발 - 빌드 버전이 없어 비교할 기준이 없다
    return;
  }

  let served: unknown;
  try {
    // no-store 로 받아야 브라우저 HTTP 캐시에 걸리지 않는다.
    // nginx 도 이 파일에 캐시 금지를 걸어 두 쪽에서 막는다
    const response = await fetch('/version.json', { cache: 'no-store' });
    if (!response.ok) {
      return;
    }
    served = await response.json();
  } catch {
    // 오프라인이거나 파일이 없다. 조용히 넘어간다 - 다음 확인 때 다시 본다
    return;
  }

  const target = versionUpdateTarget(local, served, readSession(KEY_FORCED_VERSION));
  if (target === null) {
    return;
  }
  pendingVersion = target;
  emit({ available: true, source: 'version', auto: true, version: target });
}

/**
 * 갱신 직전 버전을 남긴다. 새로고침 뒤 무엇이 바뀌었는지 알리기 위해서다.
 *
 * 🔴 새 버전의 변경 내역은 새 번들에 있다
 *   지금 실행 중인 앱은 옛 내역만 안다. 그래서 업데이트 **전에** 무엇이
 *   바뀌는지 보여줄 방법이 없다. 대신 갱신 뒤에 "이렇게 바뀌었습니다" 로 알린다.
 */
function rememberCurrentVersion(): void {
  const current = readAppVersion(import.meta.env)?.version ?? null;
  if (current !== null) {
    writeSession(KEY_PREV_VERSION, current);
  }
}

/**
 * 방금 갱신됐나. 그렇다면 직전 버전을 돌려준다. 아니면 null.
 *
 * 🔴 한 번 읽으면 표시를 지운다
 *   배너가 세션 내내 다시 뜨면 성가시다. 화면을 옮길 때마다 "업데이트됐습니다"
 *   가 반복되면 알림이 아니라 잡음이 된다.
 */
export function consumeJustUpdated(): string | null {
  const previous = readSession(KEY_PREV_VERSION);
  if (previous === null) {
    return null;
  }
  clearSession(KEY_PREV_VERSION);
  const current = readAppVersion(import.meta.env)?.version ?? null;
  if (current === null || current === previous) {
    return null;   // 갱신을 시도했지만 버전이 그대로다. 알릴 것이 없다
  }
  return previous;
}

/**
 * 강제로 최신 상태로 되돌린다. "최신으로 업데이트" 버튼과 버전 불일치 경로가 부른다.
 *
 * 🔴 사용자가 손으로 하던 절차를 코드로 한다
 *   홈 화면에서 제거 → 설정에서 사이트 데이터 삭제 → 다시 추가.
 *   그것을 여기서 서비스 워커 해제 + 캐시 삭제 + 새로고침으로 대신한다.
 *
 * 자동 갱신이 어떤 이유로 안 걸렸을 때의 탈출구다. iOS 는 이 영역에서
 * 예측이 어려워, 자동만 믿지 않고 손으로 누를 수 있는 길을 남긴다.
 *
 * 🔴 로그인은 유지된다 - 세션 쿠키와 localStorage 는 건드리지 않는다.
 *   지우는 것은 이 앱이 받아 둔 파일 캐시뿐이다.
 */
export async function forceRefresh(): Promise<void> {
  // 홈 화면 버튼에서 직접 부를 수도 있다. applyNow 를 거치지 않는 경로다
  rememberCurrentVersion();
  await detachPush();
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch {
    // 캐시를 못 지워도 새로고침은 한다. 여기서 멈추면 사용자에게 남는 수단이 없다.
  }
  // 쿼리스트링을 붙여 옛 index.html 이 HTTP 캐시에서 나오는 것까지 막는다
  window.location.replace(`${window.location.pathname}?v=${Date.now()}`);
}

/**
 * 알림 구독을 잠시 내려둔다. 강제 갱신 직전에 부른다.
 *
 * 🔴 서비스 워커를 해제하면 푸시 구독이 함께 사라진다 (2026-08-30 발견)
 *   구독은 워커 등록에 붙어 있다. 해제하면 브라우저 쪽 구독이 폐기되는데
 *   서버에는 옛 endpoint 가 남아 발송할 때마다 410 을 받는다.
 *   무엇보다 사용자에게 아무 말 없이 알림이 끊긴다 -
 *   화면은 "꺼짐"으로 보이지만 스스로 끈 적이 없으니 이유를 알 수 없다.
 *
 *   그래서 해제 전에 서버 기록을 정리하고, 새로고침 뒤 다시 켠다.
 *   🔴 endpoint 는 해제 후에는 알 수 없다. 순서가 뒤바뀌면 서버에 유령이 남는다.
 */
async function detachPush(): Promise<void> {
  try {
    if (!pushSupported()) {
      return;
    }
    const subscription = await currentSubscription();
    if (subscription === null) {
      return;
    }
    /*
      🔴 서버 행만 지운다. "켜두고 싶다" 는 의사는 건드리지 않는다 (2026-09-04)

        전에는 disablePush() 를 그대로 불렀는데, 그것은 사용자가 직접 끈 것과
        같은 처리라 의사까지 지웠다. 그래서 강제 갱신 뒤 자가 복구가
        "이 사람은 알림을 끈 것" 으로 보고 되살리지 않았다.

        여기서 하려는 것은 "잠시 내려둔다" 이지 "끈다" 가 아니다.
    */
    await api.delete<boolean>(
      `/push/subscriptions?endpoint=${encodeURIComponent(subscription.endpoint)}`,
    );
    await subscription.unsubscribe();
  } catch {
    // 정리에 실패해도 갱신은 진행한다. 화면이 옛것인 쪽이 더 큰 문제다
    // 유령 행이 남아도 다음 발송에서 410 을 받고 서버가 지운다
  }
}

/*
  sessionStorage 는 사파리 프라이빗 모드 등에서 접근만 해도 예외를 던진다.
  업데이트 감지가 그것 때문에 통째로 죽으면 안 되므로 전부 감싼다.
  값을 못 읽으면 가드가 없는 것처럼(= 평소대로) 동작한다.
*/
function readSession(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // 가드를 못 남겨도 갱신 자체는 진행한다
  }
}

function clearSession(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // 무시
  }
}
